import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography } from "@mui/material";
import * as React from "react";
import { useState, useEffect } from "react";

/**
 * Компонент для отслеживания и отображения маршрута мапинга
 * @param {Object} props
 * @param {Object} props.mapModel - Экземпляр модели мапинга
 * @param {Array} props.mapCalls - Массив вызовов мапинга [{params: [], label: string}]
 * @param {Function} props.onMappingComplete - Callback после завершения мапинга с результатом
 * @param {boolean} props.enabled - Включить отслеживание
 */
export default function MappingRouteTracker({ mapModel, mapCalls = [], onMappingComplete, enabled = true }) {
  const [mappingRoute, setMappingRoute] = useState([]);
  const [showMappingRoute, setShowMappingRoute] = useState(false);

  useEffect(() => {
    if (!enabled || !mapModel || mapCalls.length === 0) {
      return;
    }

    const runMapping = async () => {
      const route = [];
      let resultData = null;

      for (let i = 0; i < mapCalls.length; i++) {
        const call = mapCalls[i];
        const { params, label } = call;
        const clonedParams = params.map(p => (p && typeof p === 'object') ? JSON.parse(JSON.stringify(p)) : p);

        const sourceIdx = [4, 5, 1, 6, 0, 2].find(idx => clonedParams[idx] != null) ?? null;
        const targetIdx = clonedParams[3] != null ? 3 : clonedParams[2] != null ? 2 : clonedParams[0] != null ? 0 : null;

        const step = {
          step: i + 1,
          source: label || `Шаг ${i + 1}`,
          sourceData: sourceIdx != null ? clonedParams[sourceIdx] : null,
          timestamp: new Date().toISOString(),
          mappings: []
        };
        route.push(step);

        const mappings = await mapModel.map(...clonedParams, (mapping) => {
          step.mappings.push(mapping);
        });

        step.mappings = mappings || [];
        step.result = targetIdx != null ? JSON.parse(JSON.stringify(clonedParams[targetIdx])) : null;
        resultData = step.result;
      }

      setMappingRoute(route);

      if (onMappingComplete && resultData) {
        onMappingComplete(resultData);
      }
    };

    runMapping();
  }, [mapModel, mapCalls, onMappingComplete, enabled]);

  return (
    <>
      <Button 
        variant="outlined" 
        size="small"
        onClick={() => setShowMappingRoute(true)}
        disabled={mappingRoute.length === 0}
        sx={{ ml: 2 }}
      >
        Маршрут мапинга
      </Button>
      
      <Dialog 
        open={showMappingRoute} 
        onClose={() => setShowMappingRoute(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Маршрут мапинга данных</DialogTitle>
        <DialogContent>
          {mappingRoute.map((route, index) => (
            <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
              <Typography variant="h6" gutterBottom>
                Шаг {route.step}: {route.source}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Время: {new Date(route.timestamp).toLocaleString('ru-RU')}
              </Typography>
              
              {route.mappings && route.mappings.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Примененные маппинги ({route.mappings.length}):
                  </Typography>
                  <Box sx={{ 
                    background: '#fff3e0', 
                    padding: '10px', 
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}>
                    {route.mappings.map((mapping, mIndex) => (
                      <Box key={mIndex} sx={{ mb: 1.5, pb: 1.5, borderBottom: mIndex < route.mappings.length - 1 ? '1px solid #ffcc80' : 'none' }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#e65100' }}>
                          {mapping.sourceType}:{mapping.sourceMember} → {mapping.targetType}.{mapping.targetMember}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#666' }}>
                          Значение: {typeof mapping.transformedValue === 'object' 
                            ? JSON.stringify(mapping.transformedValue) 
                            : String(mapping.transformedValue)}
                        </Typography>
                        {mapping.originalValue !== undefined && mapping.originalValue !== mapping.transformedValue && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                            Исходное: {typeof mapping.originalValue === 'object'
                              ? JSON.stringify(mapping.originalValue)
                              : String(mapping.originalValue)}
                          </Typography>
                        )}
                        {mapping.targetMemberType && (
                          <Typography variant="caption" sx={{ display: 'block', color: '#888' }}>
                            Тип: {mapping.targetMemberType}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Исходные данные:
                </Typography>
                <pre style={{ 
                  background: '#f5f5f5', 
                  padding: '10px', 
                  borderRadius: '4px', 
                  overflow: 'auto',
                  maxHeight: '200px',
                  fontSize: '12px'
                }}>
                  {JSON.stringify(route.sourceData, null, 2)}
                </pre>
              </Box>
              
              {route.result && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Результат мапинга:
                  </Typography>
                  <pre style={{ 
                    background: '#e8f5e9', 
                    padding: '10px', 
                    borderRadius: '4px', 
                    overflow: 'auto',
                    maxHeight: '200px',
                    fontSize: '12px'
                  }}>
                    {JSON.stringify(route.result, null, 2)}
                  </pre>
                </Box>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowMappingRoute(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

